'use client';

import React from 'react';
import * as Lucide from 'lucide-react';
import * as Heroicons from '@heroicons/react/24/outline';

/** Map Heroicon-style names to Lucide components for sharp, modern rendering */
const HERO_TO_LUCIDE: Record<string, React.ComponentType<{ size?: number; strokeWidth?: number; className?: string; style?: React.CSSProperties }>> = {
  ChartBarIcon: Lucide.BarChart3,
  DocumentTextIcon: Lucide.FileText,
  DocumentArrowUpIcon: Lucide.Upload,
  ExclamationCircleIcon: Lucide.AlertCircle,
  ScaleIcon: Lucide.Scale,
  ArrowsRightLeftIcon: Lucide.ArrowLeftRight,
  ChartBarSquareIcon: Lucide.LayoutGrid,
  DocumentChartBarIcon: Lucide.BarChart2,
  Cog6ToothIcon: Lucide.Settings,
  ChevronLeftIcon: Lucide.ChevronLeft,
  ChevronRightIcon: Lucide.ChevronRight,
  UserCircleIcon: Lucide.UserCircle,
  UserPlusIcon: Lucide.UserPlus,
  ArrowRightOnRectangleIcon: Lucide.LogOut,
  SparklesIcon: Lucide.Sparkles,
  BoltIcon: Lucide.Zap,
  LockClosedIcon: Lucide.Lock,
  ArrowPathIcon: Lucide.RefreshCw,
  ExclamationTriangleIcon: Lucide.AlertTriangle,
  XMarkIcon: Lucide.X,
  CheckCircleIcon: Lucide.CheckCircle,
  PlusCircleIcon: Lucide.PlusCircle,
  LinkIcon: Lucide.Link,
  ArrowTopRightOnSquareIcon: Lucide.ExternalLink,
  RefreshCw: Lucide.RefreshCw,
  X: Lucide.X,
  Bars3Icon: Lucide.Menu,
  UsersIcon: Lucide.Users,
  CloudArrowUpIcon: Lucide.CloudUpload,
  BanknotesIcon: Lucide.Banknote,
  DocumentIcon: Lucide.File,
  ChevronUpDownIcon: Lucide.ChevronsUpDown,
  CheckBadgeIcon: Lucide.BadgeCheck,
  MoonIcon: Lucide.Moon,
  EnvelopeIcon: Lucide.Mail,
  BellAlertIcon: Lucide.Bell,
  InformationCircleIcon: Lucide.Info,
  ShieldCheckIcon: Lucide.ShieldCheck,
  CheckIcon: Lucide.Check,
  EyeIcon: Lucide.Eye,
  EyeSlashIcon: Lucide.EyeOff,
  KeyIcon: Lucide.Key,
  ShieldExclamationIcon: Lucide.ShieldAlert,
  ArrowRightIcon: Lucide.ArrowRight,
  FunnelIcon: Lucide.Filter,
  ClockIcon: Lucide.Clock,
  CurrencyDollarIcon: Lucide.DollarSign,
  TableCellsIcon: Lucide.Table2,
  DocumentArrowDownIcon: Lucide.Download,
  LightBulbIcon: Lucide.Lightbulb,
  ClipboardDocumentIcon: Lucide.ClipboardList,
  ArrowDownTrayIcon: Lucide.Download,
  CalculatorIcon: Lucide.Calculator,
  PaperAirplaneIcon: Lucide.Send,
  ChartPieIcon: Lucide.PieChart,
  TrashIcon: Lucide.Trash2,
};

type HeroiconName = keyof typeof Heroicons;
type IconName = HeroiconName | keyof typeof HERO_TO_LUCIDE;

interface IconProps {
  name: IconName;
  size?: number;
  className?: string;
  strokeWidth?: number;
  style?: React.CSSProperties;
  /** Forwarded to SVG element */
  'aria-hidden'?: boolean;
  title?: string;
}

const SHARP_STYLE: React.CSSProperties = {
  shapeRendering: 'geometricPrecision',
  flexShrink: 0,
};

const AppIcon = ({ name, size = 24, className = '', strokeWidth = 1.5, style, ...rest }: IconProps) => {
  const LucideIcon = HERO_TO_LUCIDE[name as keyof typeof HERO_TO_LUCIDE];
  if (LucideIcon) {
    return (
      <LucideIcon
        size={size}
        strokeWidth={strokeWidth}
        className={className}
        style={{ ...SHARP_STYLE, ...style }}
        {...rest}
      />
    );
  }
  const Heroicon = Heroicons[name as HeroiconName];
  if (Heroicon) {
    return (
      <Heroicon
        width={size}
        height={size}
        strokeWidth={strokeWidth}
        className={className}
        style={{ ...SHARP_STYLE, ...style }}
        {...rest}
      />
    );
  }
  if (process.env.NODE_ENV !== 'production') {
    console.warn(`AppIcon: "${name}" not found in Lucide or Heroicons`);
  }
  return null;
};

export default AppIcon;
